import React, { useEffect, useState } from "react";
import Sidebar from "../../components/layout/Sidebar";
import Header from "../../components/layout/Header";
import { Helmet } from "react-helmet";
import CreateUserModal from "../../components/organization/CreateUserModal";
import EditUserModal from "../../components/organization/EditUserModal";
import UsersFilters from "../../components/organization/UsersFilters";
import UsersTable from "../../components/organization/UsersTable";
import Pagination from "../../components/organization/Pagination";
import { httpRequest } from "../../utils/httpRequest";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { canWrite } from "../../components/auth/perm";
import SubscriptionManagerModal from "../../components/organization/SubscriptionManagerModal";
import { useSession } from "../../session/SessionProvider";
import SendNotificationModal from "../../components/organization/SendNotificationModal";
import ConfirmModal from "../../components/common/ConfirmModal";

function useDebouncedValue(value, delay = 500) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

const OrgUsersPage = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language.includes("fa");
  const token = JSON.parse(localStorage.getItem("user"))?.token;
  const mayCreate = canWrite(JSON.parse(localStorage.getItem("user")), "users");
  const { user } = useSession();

  // لیست و صفحه‌بندی
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [limit, setLimit] = useState(20);
  const [loading, setLoading] = useState(false);

  // فیلترها
  const [q, setQ] = useState("");
  const [tags, setTags] = useState([]);
  const [allTags, setAllTags] = useState(false);
  const [sort, setSort] = useState("-createdAt");
  const qDebounced = useDebouncedValue(q, 500);
  const [notifUser, setNotifUser] = useState(null);

  // مدال‌ها
  const [showCreate, setShowCreate] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [subUser, setSubUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);

  const fetchUsers = async (overwritePage) => {
    const finalPage = overwritePage ?? page;
    setLoading(true);
    try {
      const params = {
        q: qDebounced || "",
        tags: tags.join(","),
        allTags: allTags ? "true" : "false",
        sort,
        page: finalPage,
        limit,
      };
      const { data } = await httpRequest(
        "GET",
        "/user",
        {},
        {
          "x-access-token": token,
        },
        params,
      );
      const payload = data?.data || {};
      setItems(payload.items || []);
      setTotal(payload.total || 0);
      setPages(payload.pages || 1);
      setPage(payload.page || finalPage);
    } catch (e) {
      console.error(e);
      setItems([]);
      setTotal(0);
      setPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteUser?._id) return;

    try {
      await httpRequest(
        "DELETE",
        `/user/${deleteUser._id}`,
        {},
        {
          "x-access-token": token,
        },
      );
      setDeleteUser(null);
      fetchUsers(page); // بروزرسانی لیست پس از حذف
    } catch (e) {
      console.error("خطا در حذف کاربر:", e);
      alert(e?.response?.data?.message || "خطایی در حذف کاربر رخ داد");
    }
  };

  const applyFilters = () => fetchUsers(1);
  const resetFilters = () => {
    setQ("");
    setTags([]);
    setAllTags(false);
    setSort("-createdAt");
    setLimit(20);
    fetchUsers(1);
  };

  useEffect(() => {
    fetchUsers(1);
  }, [qDebounced, sort, limit]); // تغییر فیلترهای اصلی
  useEffect(() => {
    fetchUsers();
  }, []); // بار اول

  const mainContentClass = `flex-1 flex flex-col px-2 pb-5 lg:px-0 lg:pb-0 ${
    isRtl ? "md:mr-64" : "md:ml-64"
  }`;
  const mainSectionClass = `flex-1 overflow-y-auto p-4 md:p-6 bg-gray-100 rounded-[16px] ${
    isRtl ? "lg:me-3 lg:mb-3" : "lg:ms-3 lg:mb-3"
  }`;

  return (
    <div className="flex h-screen">
      <Helmet>
        <title>{t("org_users.page_title")}</title>
      </Helmet>

      <Sidebar />
      <div className={mainContentClass}>
        <Header />
        <main className={mainSectionClass}>
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-gray-800">
              {t("org_users.page_header")}
            </h1>
            {mayCreate && (
              <button
                onClick={() => setShowCreate(true)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                <Plus size={18} /> ایجاد کاربر
              </button>
            )}
          </div>

          <UsersFilters
            q={q}
            setQ={setQ}
            tags={tags}
            setTags={setTags}
            allTags={allTags}
            setAllTags={setAllTags}
            sort={sort}
            setSort={setSort}
            limit={limit}
            setLimit={setLimit}
            onApply={applyFilters}
            onReset={resetFilters}
            isRtl={isRtl}
          />

          <div className="mt-4">
            <UsersTable
              items={items}
              loading={loading}
              onEdit={(u) => setEditUser(u)}
              onSub={(u) => setSubUser(u)}
              onNotif={(u) => setNotifUser(u)} // ✅ اضافه شد
              onDelete={(u) => setDeleteUser(u)}
              isRtl={isRtl}
              currentUser={user}
            />
          </div>

          <Pagination
            page={page}
            pages={pages}
            onChange={(p) => {
              setPage(p);
              fetchUsers(p);
            }}
          />
        </main>
      </div>

      {/* Modals */}
      <CreateUserModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => {
          setShowCreate(false);
          fetchUsers(1);
        }}
      />

      <EditUserModal
        user={editUser}
        isOpen={!!editUser}
        onClose={() => setEditUser(null)}
        onSaved={() => {
          setEditUser(null);
          fetchUsers(page);
        }}
      />

      <SendNotificationModal
        isOpen={!!notifUser}
        onClose={() => setNotifUser(null)}
        targetUser={notifUser}
      />

      <SubscriptionManagerModal
        isOpen={!!subUser}
        onClose={() => setSubUser(null)}
        targetUser={subUser}
      />

      <ConfirmModal
        open={!!deleteUser}
        title="حذف کاربر"
        message={`آیا از حذف کاربر "${
          deleteUser?.name || deleteUser?.phone || deleteUser?.email || ""
        }" اطمینان دارید؟ این عملیات قابل بازگشت نیست.`}
        confirmText="حذف"
        cancelText="انصراف"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteUser(null)}
      />
    </div>
  );
};

export default OrgUsersPage;
